import * as z from "zod";
import { decimal, version } from "../index";
import { paymentSources } from "./enums";
import { reason } from "./fields";

// The payment gate of a sales order (M10, FR-M10-01…03, ADR-112; 50-api/sales.md §2.5): the flag "payment completed"
// Credit Control raises by hand, or the payment tool on Business Central through I-06 in F2 (ADR-234), and what
// Business Central says it received and invoiced (I-06).

/**
 * FR-M10-02: Credit Control raises the flag of an order, with the amount it saw paid and where it saw it. `version`
 * is the one the client read (409 otherwise).
 */
export const confirmPaymentInput = z.object({
  salesOrderId: z.uuid(),
  version,
  /** In the currency of the order. */
  amount: decimal.refine((value) => Number(value) > 0, "the amount paid is more than zero"),
  /** Where the payment was seen: a bank reference, a remittance. */
  reference: z.string().trim().min(1).max(200).optional(),
});
export type ConfirmPaymentInput = z.input<typeof confirmPaymentInput>;
export const confirmPaymentBody = confirmPaymentInput.omit({ salesOrderId: true });

/** A flag raised by mistake is taken back, with the reason. */
export const revokePaymentInput = z.object({ salesOrderId: z.uuid(), version, reason });
export type RevokePaymentInput = z.input<typeof revokePaymentInput>;
export const revokePaymentBody = revokePaymentInput.omit({ salesOrderId: true });

/** The confirmation the flag of an order stands on. */
export const paymentConfirmationDto = z.object({
  amount: decimal,
  currency: z.string(),
  reference: z.string().nullable(),
  /** `manual`: Credit Control raised it; otherwise the payment tool on Business Central, through I-06 (ADR-234). */
  source: z.enum(paymentSources),
  /** Name of who raised the flag; null when the payment tool did. */
  confirmedBy: z.string().nullable(),
  confirmedAt: z.iso.datetime(),
});

/**
 * What Business Central has for an order at `asOf` (I-06): totals at that moment, never differences. The invoiced,
 * paid and open amounts are of the invoices of the order.
 */
export const bcPaymentDto = z.object({
  currency: z.string(),
  depositReceivedAmount: decimal,
  invoicedAmount: decimal,
  paidAmount: decimal,
  openAmount: decimal,
  overdueAmount: decimal.nullable(),
  asOf: z.iso.datetime(),
});
